/**
 * Media Upload Utilities
 * 
 * This file contains utilities for uploading media files using the Path Registry system.
 * It provides a universal approach for all media uploads, leveraging the path definitions
 * stored in the registry.
 */

import axios from 'axios';
import { logError, getUserFriendlyErrorMessage } from './errorLoggingUtils';
import { getPathDefinition } from './pathRegistryUtils';
import { applyDefaultParams, getCriticalParams, getResourceTypeDefaults } from './mediaUploadConfig';
import { config } from '../constants/config';

/**
 * Uploads a media file using the Path Registry system
 * @param {File} file - The file to upload
 * @param {string} resourceType - The resource type (path definition name)
 * @param {Object} params - Parameters for the path
 * @param {string} userId - The user ID
 * @returns {Promise<string>} - The URL of the uploaded media
 */
export const uploadMedia = async (file, resourceType, params, userId) => {
  console.log('uploadMedia called with:', {
    file: file ? { name: file.name, size: file.size, type: file.type } : null,
    resourceType,
    params,
    userId
  });

  // Validate basic required parameters
  if (!file) {
    throw new Error('File is required for media upload');
  }
  if (!resourceType) {
    throw new Error('Resource type is required for media upload');
  }
  if (!params) {
    throw new Error('Parameters are required for media upload');
  }
  if (!userId) {
    throw new Error('User ID is required for media upload');
  }

  try {
    // Use the Path Registry system
    console.log('Using Path Registry system for media upload');

    // Add userId to params
    let allParams = {
      ...params,
      userId,
      resourceType // Add resourceType to allParams

    };

    // Add fileName if not provided
    if (!allParams.fileName && file.name) {
      allParams.fileName = file.name;
    }

    // Apply default parameters from config
    allParams = await applyDefaultParams(resourceType, allParams);

    // Get critical parameters for this resource type
    const criticalParams = await getCriticalParams(resourceType);
    console.log(`Critical parameters for ${resourceType}:`, criticalParams);

    // Log the default values being applied
    const defaultsBeforeMerge = await getResourceTypeDefaults(resourceType);
    console.log(`Default values for ${resourceType}:`, defaultsBeforeMerge);
    console.log(`Parameters after applying defaults:`, allParams);

    // Create FormData
    // IMPORTANT: Text fields MUST be appended BEFORE the file.
    // Multer's key() callback fires as soon as it encounters the file field in
    // the multipart stream.  If the file comes first, req.body is still empty
    // and the server falls back to file.originalname instead of the alias.
    const formData = new FormData();

    // Add resourceType and all parameters to the form data (before file!)
    formData.append('resourceType', resourceType);

    // Add all parameters as form data fields
    Object.keys(allParams).forEach(key => {
      formData.append(key, allParams[key]);
    });

    // Append the file LAST so multer has access to body params in its key() function
    formData.append('file', file);

    // Build query parameters for the URL
    const queryParams = [];

    // Add all critical parameters to query params
    criticalParams.forEach(paramName => {
      if (allParams[paramName]) {
        queryParams.push(`${paramName}=${encodeURIComponent(allParams[paramName])}`);
      }
    });

    // Use the universal media upload endpoint with query parameters
    const url = `${config.API_MEDIA_UPLOAD}?${queryParams.join('&')}`;

    // Log the upload attempt
    console.log(`Uploading ${resourceType} media using Path Registry:`, {
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
      resourceType,
      params: allParams,
      url: url
    });

    // Get the authentication token from localStorage
    // Try both 'token' and 'authToken' keys since different parts of the app might use different keys
    const token = localStorage.getItem('authToken') || localStorage.getItem('token');

    // Make the upload request
    const response = await axios.post(
      url,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': token ? `Bearer ${token}` : ''
        }
      }
    );

    // Check different possible response formats
    let fileUrl = '';
    if (response.data) {
      if (response.data.fileUrl) {
        fileUrl = response.data.fileUrl;
      } else if (response.data.url) {
        fileUrl = response.data.url;
      } else if (typeof response.data === 'string' && response.data.includes('http')) {
        fileUrl = response.data;
      }
    }

    console.log(`Successfully uploaded ${resourceType} media:`, fileUrl);
    return fileUrl;
  } catch (error) {
    // Log detailed error information
    const errorDetails = logError(error, `uploading ${resourceType} media`, {
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
      resourceType,
      params
    }, {
      userId,
      timestamp: new Date().toISOString()
    });

    // Create a user-friendly error message
    const userMessage = getUserFriendlyErrorMessage(error, `uploading ${resourceType}`);

    // Create an enhanced error object with both detailed and user-friendly information
    const enhancedError = new Error(userMessage);
    enhancedError.originalError = error;
    enhancedError.details = errorDetails;
    enhancedError.userMessage = userMessage;

    throw enhancedError;
  }
};

/**
 * Helper function to determine if a file needs to be uploaded
 * @param {Object|string} fileValue - The file value from a form
 * @returns {boolean} - Whether the file needs to be uploaded
 */
export const needsUpload = (fileValue) => {
  return fileValue &&
    typeof fileValue === 'object' &&
    fileValue.file instanceof File;
};

/**
 * Helper function to get the original URL from a file value
 * @param {Object|string} fileValue - The file value from a form
 * @returns {string|null} - The original URL or null
 */
export const getOriginalUrl = (fileValue) => {
  if (!fileValue) {
    return null;
  }

  if (typeof fileValue === 'string') {
    return fileValue;
  }

  if (typeof fileValue === 'object') {
    return fileValue.originalUrl || fileValue.previewUrl || null;
  }

  return null;
};
